// src/components/layout/Header.jsx
/* ========================
 * Header - Main application header component
 * Mobile-optimized with responsive button visibility and enhanced light mode styling
 * ========================*/
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { debounce } from 'lodash';
import { CatLogo } from '@/components/CatLogo';
import { SettingsPanel } from '@/components/layout/settings/SettingsPanel';
import { HousePortal } from '@/App';
import { MenuIcon, Settings, Lightbulb, X } from 'lucide-react';
import HeaderActionButtons from './HeaderActionButtons';
import { useTheme } from '@/managers/ThemeProvider';
import { authManager } from '@/managers/AuthManager';
import { usePanelManager, PanelTypes, RightSidebarViews } from '@/managers/PanelManager';
import { getPlatformDisplayName } from '@/utils/platformDisplay';
import { keyStateManager } from '@/managers/zKeysManager';

const MOBILE_BREAKPOINT = 768;

/* ========================
 * LeftSection - Left section of the header
 * Contains sidebar toggle, settings toggle, and app logo/info with proper theme styling
 * ========================*/
const LeftSection = React.memo(function LeftSection({
  onSidebarOpen,
  setIsSettingsOpen,
  isSettingsOpen,
  currentConversation,
  isCompactLayout,
  isMobileView
}) {
  const { isLightTheme } = useTheme();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasKeys, setHasKeys] = useState(() => keyStateManager.getState().hasKeys);
  const currentConversationPlatformLabel = getPlatformDisplayName(currentConversation?.botPlatform);

  useEffect(() => {
    const unsub = keyStateManager.subscribe((s) => setHasKeys(s.hasKeys));
    return () => unsub();
  }, []);

  useEffect(() => {
    const handleAuthState = (state) => {
      if (state.settingsLoaded) {
        setShowOnboarding(!state.settings.zPreferences.onboardingHeaderFinished);
      }
    };
    const authUnsubscribe = authManager.subscribe(handleAuthState);
    return () => authUnsubscribe();
  }, []);

  const handleDismissOnboarding = () => {
    setShowOnboarding(false);
    authManager.updateSettings({ zPreferences: { onboardingHeaderFinished: true } });
  };

  const handleSidebarOpen = () => {
    if (showOnboarding) handleDismissOnboarding();
    onSidebarOpen();
  };

  return (
    <div className="relative flex items-center h-[50px] gap-1">
      {/* Onboarding Bubble for Header */}
      {showOnboarding && (
          <div
              className="absolute top-full left-0 mt-4 w-[280px] z-[1000] rounded-lg bg-background"
              style={{ pointerEvents: 'auto' }}
          >
              <div className="relative drop-shadow-xl">
                  <div
                      className="relative flex items-start gap-3 p-4 rounded-lg border border-border-color bg-surface"
                  >
                      {/* Pointer 1 (for Menu Icon) */}
                      <div
                          className="absolute w-3 h-3 bg-surface border-t border-l border-border-color"
                          style={{
                              top: '-7px',
                              left: '22px',
                              transform: 'translateX(-50%) rotate(45deg)',
                          }}
                      />
                      {/* Pointer 2 (for Settings Icon) */}
                      <div
                          className="absolute w-3 h-3 bg-surface border-t border-l border-border-color"
                          style={{
                              top: '-7px',
                              left: '56px',
                              transform: 'translateX(-50%) rotate(45deg)',
                          }}
                      />
                      <Lightbulb className="w-5 h-5 text-primary flex-shrink-0" />
                      <p className="flex-1 text-sm text-on-surface leading-relaxed mt-px">
                          Access more models, chat history, preferences, and subscription details here.
                      </p>
                      <button
                          onClick={handleDismissOnboarding}
                          className="-mt-1 -mr-1 p-1.5 text-on-surface-dim hover:text-on-surface rounded-full transition-colors flex-shrink-0"
                          aria-label="Dismiss"
                      >
                          <X className="w-4 h-4" />
                      </button>
                  </div>
              </div>
          </div>
      )}
      <button
        onClick={handleSidebarOpen}
        className={`px-3 rounded-md h-full flex items-center transition-colors duration-200
          ${isLightTheme
            ? 'hover:bg-white/20 text-white'
            : 'hover:bg-surface-dark text-on-surface'}`}
        aria-label="Open history"
      >
        <MenuIcon className="w-5 h-5" />
      </button>
      <button
        onClick={() => setIsSettingsOpen(true)}
        className={`mr-1 rounded-md h-full flex items-center cursor-pointer transition-colors duration-200
          ${isSettingsOpen
            ? 'text-white'
            : (isLightTheme ? 'text-white hover:text-white/80' : 'text-on-surface hover:text-primary-light')}`}
        aria-label="Open settings"
      >
        <Settings className="w-4 h-4" />
      </button>
      <div className="flex items-center h-full">
        <div className="peer h-full flex items-center">
          <button
            onClick={handleSidebarOpen}
            aria-label="Open history"
            className="h-full flex items-center"
          >
            <CatLogo
              size={22}
              withText={true}
              forceColorClass={isLightTheme ? "text-white" : undefined}
            />
          </button>
        </div>
        {currentConversation && !isMobileView && (
          <div className={`
            flex flex-col justify-center h-full ml-3
            transform transition-all duration-300 ease-out
            translate-x-0 ${!isCompactLayout ? 'peer-hover:translate-x-[108px]' : ''}
          `}>
            <button
              onClick={handleSidebarOpen}
              className="relative text-left"
            >
              <div className={`text-[7px] font-medium uppercase tracking-[0.15em] leading-none ${isCompactLayout ? 'hidden' : 'block'}
                ${isLightTheme ? 'text-white/70' : 'text-primary/60'}`}>
                Current Assistant
              </div>
              <div className="leading-none">
                <span className={`text-[11px] font-semibold
                  ${isLightTheme ? 'text-white' : 'text-on-surface/90'}`}>
                  {currentConversation.botName}
                </span>
                <div className={`text-[8px] mt-1 truncate max-w-[140px]
                  ${isLightTheme ? 'text-white/80' : 'text-on-surface/70'}`}>
                  <span className={isCompactLayout ? 'hidden' : 'inline'}>via </span>{hasKeys ? (currentConversation.botCleanModelName?.toLowerCase().startsWith(currentConversationPlatformLabel?.toLowerCase()) ? currentConversation.botCleanModelName : `${currentConversationPlatformLabel} ${currentConversation.botCleanModelName}`) : 'Add API Key to Activate'}
                </div>
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
});
LeftSection.displayName = 'LeftSection';

/* ========================
 * Header - Main Header Component
 * Manages overall header structure and state with mobile optimization and proper theme styling
 * ========================*/
const Header = ({
  onSidebarOpen,
  onNewChatClick,
  onSnippetsClick,
  onThemeToggle,
  onLogout,
  isSnippetsOpen = false,
  isMemorySliderOpen,
  onMemorySliderToggle,
  sliderRef,
  currentConversation,
  hasNewCompletion,
  showLogout = true,
}) => {
  const { isLightTheme } = useTheme();
  const { isPanelOpen } = usePanelManager();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [useDevSettings, setUseDevSettings] = useState(() => authManager.getState().settings?.zPreferences?.useDevSettings || false);
  
  const anyPanelOpen = useMemo(() => Object.values(PanelTypes).some(type => isPanelOpen(type)), [isPanelOpen]);
  const isMobileView = windowWidth < MOBILE_BREAKPOINT;
  const isCompactLayout = anyPanelOpen || windowWidth < 830;

  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowWidth(window.innerWidth);
    }, 100);

    window.addEventListener('resize', handleResize);
    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const handleAuthState = (state) => {
        if (state.settingsLoaded) {
            setUseDevSettings(state.settings.zPreferences?.useDevSettings || false);
        }
    };
    const authUnsubscribe = authManager.subscribe(handleAuthState);
    handleAuthState(authManager.getState()); // Initial check on mount
    return () => authUnsubscribe();
  }, []);

  useEffect(() => {
    if (!isSettingsOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsSettingsOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isSettingsOpen]);

  const memoizedActionButtons = useMemo(() => (
    <HeaderActionButtons
      isMemorySliderOpen={isMemorySliderOpen}
      onMemorySliderToggle={onMemorySliderToggle}
      sliderRef={sliderRef}
      onNewChatClick={onNewChatClick}
      onSnippetsClick={onSnippetsClick}
      onThemeToggle={onThemeToggle}
      onLogout={onLogout}
      isSnippetsOpen={isSnippetsOpen}
      isMobileView={isMobileView}
      useDevSettings={useDevSettings}
      hasNewCompletion={hasNewCompletion}
      showLogout={showLogout}
    />
  ), [
    isMemorySliderOpen, onMemorySliderToggle, sliderRef,
    onNewChatClick, onSnippetsClick, onThemeToggle, onLogout, isSnippetsOpen,
    isMobileView, useDevSettings, hasNewCompletion, showLogout,
  ]);

  const headerClasses = `flex items-center h-[50px] px-2 transition-all duration-300 ease-in-out
    ${isLightTheme
      ? 'bg-primary text-white border-b border-primary-dark/20 shadow-sm'
      : 'bg-surface border-b border-border-color'}`;

  return (
    <>
      <header className={headerClasses}>
        <LeftSection
          onSidebarOpen={onSidebarOpen}
          setIsSettingsOpen={setIsSettingsOpen}
          isSettingsOpen={isSettingsOpen}
          currentConversation={currentConversation}
          isCompactLayout={isCompactLayout}
          isMobileView={isMobileView}
        />
        <div className="flex-1 flex justify-end overflow-visible">
          {memoizedActionButtons}
        </div>
      </header>
      <HousePortal>
        <SettingsPanel
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
        />
      </HousePortal>
    </>
  );
};

export default React.memo(Header);